import { useNavigate } from "react-router-dom";
import useUserContext, { UserContextActions } from "../contexts/UserContext";
import useAuthHelper from "./useAuthHelper";

export default function useAuthService() {
  const navigate = useNavigate();
  const { post } = useAuthHelper();
  const { dispatch } = useUserContext();
  const lsKey = "estatetrack-user";

  const getTokenExpiration = (identity) => {
    // The auth API serialises jwtTokenExpiration as a naive datetime string
    // (UK wall-clock, no timezone marker), which the browser parses in the
    // user's local zone - for non-UK users the token then looks expired and
    // triggers an endless refresh loop. The JWT's own exp claim is a unix
    // timestamp, so prefer that and only fall back to the string.
    try {
      const base64 = identity.jwtToken
        .split(".")[1]
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      const payload = JSON.parse(atob(base64));
      if (payload.exp) {
        return payload.exp * 1000;
      }
    } catch (err) {
      // fall through to the expiration string
    }
    return new Date(identity.jwtTokenExpiration).getTime();
  };

  const setTokenRefresh = (tokenReponse) => {
    let refreshTimeout;
    // Refresh 5s before expiry, but never sooner than 30s from now so a bad
    // expiration value can never hammer the auth API in a tight loop.
    // Math.max would propagate NaN (e.g. no jwtToken and an unparseable
    // expiration string), so guard non-finite values explicitly.
    let timeToExp = getTokenExpiration(tokenReponse) - Date.now() - 5000;
    timeToExp = Number.isFinite(timeToExp) ? Math.max(timeToExp, 30000) : 30000;
    refreshTimeout = setTimeout(refreshToken, timeToExp);
    dispatch({ type: UserContextActions.storeTimeout, payload: refreshTimeout })
  };

  const refreshToken = () => {
    // console.log("refreshing token");
    let user = getUser();
    if (user && user.identity) {
      post(`/auth/refresh-token/${user.identity.id}`)
        .then((result) => {
          setTokenRefresh(result);
          dispatch({ type: UserContextActions.setIdentity, payload: result });
          return result;
        })
        .catch((err) => {
          console.error(err);
          return false;
        });
    } else {
      return false;
    }
  };

  const getUser = () => {
    let user = localStorage.getItem(lsKey);
    if (!user) {
      return false;
    } else {
      let userJson = JSON.parse(user);
      return userJson;
    }
  };

  const isTokenValid = (identity) => {
    if (!identity) {
      return false;
    }
    return getTokenExpiration(identity) - Date.now() - 1000 > 0;
  };

  return {
    getUser,
    refreshToken,
    setTokenRefresh,
    isTokenValid,
  };
}
